// TODO:
// - Correct zoom
// - Correct reset of zoom
// - Store the state of the widget for when we return to it
// - use schema validators
// =======================================================================================================
// React
import * as React from "react";
import _ from 'lodash';

// Internal components
import { DebugEx } from "@Utils/DebugEx"
import * as WidgetsThingsStructs from "../WidgetsThingsStructs";
import { IWidgetThingsClass } from '../Utils/IWidgetThingsClass';
import { isDefined, IsNullOrEmpty } from '@Utils/Micro';
import { SettingsGroup, SettingsType } from '@Primitive/SettingsListType';
import { DomUtils } from '@Utils/Dom';
import { SettingsList } from '@Primitive/SettingsList';
import { JsonSchemaValidator } from '@Utils/JsonSchemaValidator';
import { IWidgetDescription, IWidgetSettingsProps, WidgetDescriptionToPlegmaThing } from "../../WidgetStructs";
import { IWidgetDataValidateResp } from "../../IWidgetDataValidateResp";
import { Plegma } from '@API/Api';
import { WebThing, WebThingPort } from '@API/WebThing';
import { WebThingPortType } from '@API/WebNodeTypes';
import { Localization } from '@Primitive/Localization/Localization';
import { CVViewerWidgetMapKey, defaultLabelForUILabelKey } from './CVViewerLocalization';

const CVViewer = React.lazy(() => import("./CVViewerWidget"));

// =======================================================================================================

// ------------------------------------------------------------------------------------------------------
export const enum CVViewer_ImageFit {
    Contain = "Contain",
    Cover = "Cover",
    FillWidth = "FillWidth",
    FillHeight = "FillHeight",
    Fill = "Fill"
}
// ------------------------------------------------------------------------------------------------------
export interface CVViewerData {
    DefaultImageURL: string;
    MouseMove: boolean;
    MouseZoom: boolean;
    ShowToolbar: boolean;
    EnableRegionUtils: boolean;
    HeatmapThreshold: number;
    ImageFit: CVViewer_ImageFit;
}
// =======================================================================================================
// Settings
// =======================================================================================================
interface CVViewerWidgetSettingsState {
    WidgetData: CVViewerData;
}
// ---------------------------------------------------------------------------------------------------

class CVViewerWidgetSettings extends React.Component<IWidgetSettingsProps<CVViewerData>, CVViewerWidgetSettingsState> {
    constructor(props: IWidgetSettingsProps<CVViewerData>) {
        super(props);

        this.state = {
            WidgetData: _.cloneDeep(this.props.WidgetData),
        };

        // Bind functions
        this._saveData = this._saveData.bind(this);
    }
    // ---------------------------------------------------------------------------------------------------
    private _saveData() {
        if (isDefined(this.props.onWidgetDataUpdate))
            this.props.onWidgetDataUpdate(this.state.WidgetData);
    }
    // ------------------------------------------------------------------------------------------------
    render() {
        let settings: SettingsGroup[] = [{
            name: "",
            elements: [
                {
                    type: SettingsType.ImageFileInput,
                    label: "Default Image",
                    tooltip: 'Image to show when input port has no data.',
                    dataPath: "DefaultImageURL",
                    previewImg: true,
                    externalURL: true,
                    fileInterceptor: (value: string) => {
                        return new Promise<string>((resolve, reject) => {
                            DomUtils.getResizedImage(value, 1920, 1080, "jpeg")
                                .then((res) => { resolve(res.src); })
                                // handle error
                                .catch((er) => { reject(er) });
                        });
                    }
                },
                {
                    type: SettingsType.DropDown,
                    label: "Image Fit",
                    tooltip: 'Image fit policy. Available options (Contain,Cover,FillWidth,FillHeight.Fill)',
                    dataPath: "ImageFit",
                    items: [
                        { value: CVViewer_ImageFit.Contain, label: 'Contain' },
                        { value: CVViewer_ImageFit.Cover, label: 'Cover' },
                        { value: CVViewer_ImageFit.FillWidth, label: 'Fill Width' },
                        { value: CVViewer_ImageFit.FillHeight, label: 'Fill Height' },
                        { value: CVViewer_ImageFit.Fill, label: 'Fill' },
                    ],
                },
                {
                    type: SettingsType.Boolean,
                    label: "Support Zoom",
                    tooltip: 'Whether to support zoom or not.',
                    dataPath: "MouseZoom",
                },
                {
                    type: SettingsType.Boolean,
                    label: "Support Image drag",
                    tooltip: 'The button toolbar will have a button for toggling drag mode.',
                    dataPath: "MouseMove",
                },
                {
                    type: SettingsType.Boolean,
                    label: "Show Toolbar",
                    tooltip: 'Show button toolbar on the top of widget or not.',
                    dataPath: "ShowToolbar",
                },
                {
                    type: SettingsType.Boolean,
                    label: "Regions Support",
                    tooltip: 'Whether to show cv regions or not.',
                    dataPath: "EnableRegionUtils",
                },
                {
                    type: SettingsType.Slider,
                    label: "Heatmap Threshold",
                    dataPath: "HeatmapThreshold",
                    minValue: 0, maxValue: 100, stepValue: 5,
                    valuePosfix: "%"
                },
            ]
        }];

        return <SettingsList
            AutoSave={true}
            SettingsMap={settings}
            Data={this.state.WidgetData}
            onSaveData={(data) => this.setState({ WidgetData: data }, () => this._saveData())}
            Toolbar={this.props.SettingListToolbar}
        />;
    }
    // ---------------------------------------------------------------------------------------------------
}
// ---------------------------------------------------------------------------------------------------

// =======================================================================================================
// Widget
// =======================================================================================================
function HeatmapJsonSchemeValidator() {
    return JsonSchemaValidator.GetInstance("CVViewer_Heatmap_JsonSchemeValidator", {
        title: "HeatmapJsonSchemeValidator",
        type: 'object',
        properties: {
            Grid: {
                type: "object",
                properties: {
                    x: { type: "number" },
                    y: { type: "number" },
                }
            },
            HotArea: {
                type: "array",
                items: {
                    type: "array",
                    items: {
                        type: "number"
                    }
                }
            },
        },
        required: ["Grid", "HotArea"]
    });
}
// ---------------------------------------------------------------------------------------------------
function SetItemsJsonSchemeValidator() {
    return JsonSchemaValidator.GetInstance("CVViewer_SetItems_JsonSchemeValidator", {
        title: "SetItemsJsonSchemeValidator",
        type: "array",
        items: {
            type: 'object',
            properties: {
                type: { enum: ["img", "rect", "text"] },
                x: { type: "number" },
                y: { type: "number" },
                width: { type: "number" },
                height: { type: "number" },
                transformation: {
                    type: "object",
                    properties: {
                        rotation: { type: "number" },
                        offset: {
                            type: "object",
                            properties: {
                                x: { type: "number" },
                                y: { type: "number" },
                            },
                            required: ["x", "y"]
                        },
                    }
                },
            },
            required: ["type", "x", "y", "width", "height", "transformation"]
        }
    });
}
// ---------------------------------------------------------------------------------------------------
function SetRegionsJsonSchemeValidator() {
    return JsonSchemaValidator.GetInstance("CVViewer_SetRegions_JsonSchemeValidator", {
        title: "SetRegionsJsonSchemeValidator",
        type: "array",
        items: {
            type: 'object',
            properties: {
                id: { type: "string" },
                x: { type: "number" },
                y: { type: "number" },
                width: { type: "number" },
                height: { type: "number" },
            },
            required: ["id", "x", "y", "width", "height"]
        }
    });
}
// ---------------------------------------------------------------------------------------------------
function MsFaceJsonSchemeValidator() {
    return JsonSchemaValidator.GetInstance("CVViewer_MsFace_JsonSchemeValidator", {
        title: "MsFaceJsonSchemeValidator",
        type: "array",
        items: {
            type: 'object',
            properties: {
                faceId: { type: "string" },
                faceRectangle: {
                    type: "object",
                    properties: {
                        top: { type: "number" },
                        left: { type: "number" },
                        width: { type: "number" },
                        height: { type: "number" },
                    }
                },
                faceAttributes: {
                    type: "object",
                    properties: {
                        age: { type: "number" },
                        smile: { type: "number" },
                        gender: { type: "string" },
                        facialHair: {
                            type: "object",
                            properties: {
                                moustache: { type: "number" },
                                beard: { type: "number" },
                                sideburns: { type: "number" },
                            }
                        },
                        glasses: { type: "string" },
                    },
                }
            },
            required: ["faceId", "faceRectangle", "faceAttributes"]
        }
    });
}
// ------------------------------------------------------------------------------------------------
export const enum CVViewerPortKey {
    Img = "img",
    MsFace = "ms_face",
    Heatmap = "heatmap",
    SetRegions = "setregions",
    SetItems = "setitems",
    SaveRegions = "saveregions",
    SelectedRegion = "selectedregion"
}
// ------------------------------------------------------------------------------------------------
// Description interface
// ------------------------------------------------------------------------------------------------
export class CVViewerWidget extends IWidgetThingsClass<CVViewerData> {
    // ------------------------------------------------------------------------------------------------
    public Render(key: string, width: number, height: number): React.ReactNode {
        return <WidgetsThingsStructs.LazyWidget>
            <CVViewer />
        </WidgetsThingsStructs.LazyWidget>
    }
    // ------------------------------------------------------------------------------------------------
    public RenderWidgetSettings(key: string, width: number, height: number): React.ReactElement<any> {
        const parent = this.Parent;
        return <CVViewerWidgetSettings
            key={"Settings-" + key}
            width={width}
            height={height}
            WidgetData={_.cloneDeep(this.WidgetData)} // copy of the data and is return the copy in the update cb
            WidgetDefaultValues={_.cloneDeep(this.Description.DefaultValues)}
            onWidgetDataUpdate={(d) => this.WidgetDataUpdate(d)}
            ThingKey={this.ThingKey}
            NodeKey={this.NodeKey}
            SettingListToolbar={isDefined(parent) ? parent.SettingsListToolbar : null}
        />;
    }
    // ------------------------------------------------------------------------------------------------
    initThing() {
        // Thing description
        let plegmaThing = WidgetDescriptionToPlegmaThing(this.Description);

        // Ports
        let ports: WebThingPort[] = [];

        // Image
        ports.push(new WebThingPort(
            {
                Name: "Image",
                PortKey: CVViewerPortKey.Img,
                Type: Plegma.ePortType.String,
                ioDirection: Plegma.ioPortDirection.Input,
                ConfFlags: Plegma.ePortConf.None,
                Description: "",
                State: ""
            },
            WebThingPortType.Data,
            (data) => this.ValidateValues(CVViewerPortKey.Img, data)
        ));

        // Face
        ports.push(new WebThingPort(
            {
                Name: "MS Face",
                PortKey: CVViewerPortKey.MsFace,
                Type: Plegma.ePortType.JsonString,
                ioDirection: Plegma.ioPortDirection.Input,
                ConfFlags: Plegma.ePortConf.None,
                Description: "",
                State: ""
            },
            WebThingPortType.Data,
            (data) => this.ValidateValues(CVViewerPortKey.MsFace, data)
        ));

        // Heatmap
        ports.push(new WebThingPort(
            {
                Name: "Heatmap",
                PortKey: CVViewerPortKey.Heatmap,
                Type: Plegma.ePortType.JsonString,
                ioDirection: Plegma.ioPortDirection.Input,
                ConfFlags: Plegma.ePortConf.None,
                Description: "",
                State: ""
            },
            WebThingPortType.Data,
            (data) => this.ValidateValues(CVViewerPortKey.Heatmap, data)
        ));

        // Set regions
        ports.push(new WebThingPort(
            {
                Name: "Set Regions",
                PortKey: CVViewerPortKey.SetRegions,
                Type: Plegma.ePortType.JsonString,
                ioDirection: Plegma.ioPortDirection.Input,
                ConfFlags: Plegma.ePortConf.None,
                Description: "",
                State: ""
            },
            WebThingPortType.Data,
            (data) => this.ValidateValues(CVViewerPortKey.SetRegions, data)
        ));

        // Set Items
        ports.push(new WebThingPort(
            {
                Name: "Set Items",
                PortKey: CVViewerPortKey.SetItems,
                Type: Plegma.ePortType.JsonString,
                ioDirection: Plegma.ioPortDirection.Input,
                ConfFlags: Plegma.ePortConf.None,
                Description: "",
                State: ""
            },
            WebThingPortType.Data,
            (data) => this.ValidateValues(CVViewerPortKey.SetItems, data)
        ));

        // Save regions
        ports.push(new WebThingPort(
            {
                Name: "Save Regions",
                PortKey: CVViewerPortKey.SaveRegions,
                Type: Plegma.ePortType.JsonString,
                ioDirection: Plegma.ioPortDirection.Output,
                ConfFlags: Plegma.ePortConf.None,
                Description: "",
                State: ""
            },
            WebThingPortType.Trigger
        ));

        // Selected regions
        ports.push(new WebThingPort(
            {
                Name: "Selected Region",
                PortKey: CVViewerPortKey.SelectedRegion,
                Type: Plegma.ePortType.JsonString,
                ioDirection: Plegma.ioPortDirection.Output,
                ConfFlags: Plegma.ePortConf.None,
                Description: "",
                State: ""
            },
            WebThingPortType.Data
        ));

        // Create the thing
        let thing = new WebThing(this.WebModule, plegmaThing, ports);
        this.ThingKey = thing.ThingKey;
    }
    // ------------------------------------------------------------------------------------------------
    public static GetDefaultWidgetDataStatic(): CVViewerData {
        return _.cloneDeep({
            DefaultImageURL: "",
            MouseMove: true,
            MouseZoom: true,
            ShowToolbar: true,
            HeatmapThreshold: 0,
            EnableRegionUtils: false,
            ImageFit: CVViewer_ImageFit.Contain
        });
    }
    // ------------------------------------------------------------------------------------------------
    public GetDefaultWidgetData() { return CVViewerWidget.GetDefaultWidgetDataStatic(); }
    // ------------------------------------------------------------------------------------------------
    public static GetWidgetDesc(): IWidgetDescription {
        let data: CVViewerData = CVViewerWidget.GetDefaultWidgetDataStatic();
        return {
            Title: "CV Viewer",
            WidgetTitle: "",
            Key: "CVViewerWidget",
            Type: "CVViewerWidget",
            Data: data,
            Description: "Computer Vision Viewer",
            MinH: 1,
            MinW: 1,
            ShowExtraMenu: false,
            IconURI: WidgetsThingsStructs.IconURIPath + "/png/vision.png"
        };
    }
    // ------------------------------------------------------------------------------------------------
    ValidateValues(key: string, value: any): IWidgetDataValidateResp {
        switch (key) {
            case CVViewerPortKey.Img:
                break;
            case CVViewerPortKey.MsFace:
                return MsFaceJsonSchemeValidator().DataValidateString(value);
            case CVViewerPortKey.Heatmap:
                return HeatmapJsonSchemeValidator().DataValidateString(value);
            case CVViewerPortKey.SetItems:
                return SetItemsJsonSchemeValidator().DataValidateString(value);
            case CVViewerPortKey.SetRegions:
                return SetRegionsJsonSchemeValidator().DataValidateString(value);
            default:
                DebugEx.TraceCritical("Should not be here. CVViewer: unhandled data validation for PortKey: " + key);
                break;
        }
        return {
            valid: true,
            errors: "",
        };
    };
    // ------------------------------------------------------------------------------------------------
    public GetLocalizationDictionary() {
        let res = {};
        if (!IsNullOrEmpty(this.Description.WidgetTitle))
            res[WidgetsThingsStructs.WidgetLocalizationKeys.Title] = this.Description.WidgetTitle;

        return res;
    }
    // ------------------------------------------------------------------------------------------------
    /**
     * Return label per each button key. 
     * Default values are taken from dictionary: `defaultLabelForUILabelKey`
     */
    public static GetLabelsFromLocalizationKey(localizationKey: string): { [key: string]: string } {
        // get default values
        const defaultValues = defaultLabelForUILabelKey;

        // check for optional `localizationKey`
        if (!isDefined(localizationKey)) {
            // return default value
            return defaultValues;
        }

        // try get localization instance
        const localization = Localization.GetInstance(localizationKey);

        if (!isDefined(localization)) {
            DebugEx.TraceError("CVViewer: passed `localizationKey`: " + localizationKey + " but could not find localization instance!");
            // fallback to defaults
            return defaultValues;
        }

        // instance found; try get text map
        const map = localization.GetTextMap(CVViewerWidgetMapKey);
        if (!isDefined(map)) {
            DebugEx.TraceError("CVViewer: passed `localizationKey`: " + localizationKey + " but could not get text map.");
            // fallback to defaults
            return defaultValues;
        }

        // ok! text map found! let's update response
        const res = _.cloneDeep(defaultValues);
        _.forEach(map, (value, key) => {
            if (!IsNullOrEmpty(value))
                res[key] = value;
        });

        return res;
    }
    // ------------------------------------------------------------------------------------------------ --- Localization
}
// ------------------------------------------------------------------------------------------------